import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  cn(
    "inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-bold whitespace-nowrap transition-all outline-none",
    "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
    "disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40",
    // aria-disabled でもスタイルを適用（Button asChild で <a> などになる場合に対応）
    "aria-disabled:pointer-events-none aria-disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0",
    // ref: https://github.com/startupkenpo/mykenpo/pull/1495
    "cursor-pointer disabled:cursor-default aria-disabled:cursor-default",
  ),
  {
    variants: {
      variant: {
        default: "border bg-white text-monotone-9 hover:bg-monotone-1",
        primary:
          "bg-primary text-primary-foreground hover:bg-primary-6 active:bg-primary-7",
        secondary: cn(
          "border border-primary-6 bg-white text-primary-6 hover:bg-primary-0.5 active:bg-primary-1",
          "aria-invalid:text-destructive aria-invalid:hover:bg-destructive/5 aria-invalid:focus-visible:bg-destructive/10 aria-invalid:active:bg-destructive/10",
        ),
        destructive:
          "border border-destructive text-destructive hover:bg-error-pale",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3 font-light",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
